import { createSlice } from '@reduxjs/toolkit';
import { applyPromoCode, removePromoCode, fetchZellePayments } from '../thunk/checkoutThunk';

const initialState = {
    deliveryDates: [],
    selectedDeliveryDate: null,
    loading: false,
    error: null,
    deliveryCount: 0,
    promoCodeData: null,
    promoCodeLoading: false,
    promoCodeError: null,
    appliedPromoCode: null,
    promoCodeSuccess: null,
    zellePayments: [],
    zellePaymentsLoading: false,
    zellePaymentsError: null,
    selectedZellePaymentId: null,
};

const checkoutSlice = createSlice({
    name: 'checkout',
    initialState,
    reducers: {
        setSelectedDeliveryDate: (state, action) => {
            state.selectedDeliveryDate = action.payload;
        },
        clearCheckoutData: (state) => {
            state.deliveryDates = [];
            state.selectedDeliveryDate = null;
            state.error = null;
            state.deliveryCount = 0;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearPromoCodeError: (state) => {
            state.promoCodeError = null;
        },
        clearPromoCodeSuccess: (state) => {
            state.promoCodeSuccess = null;
        },
        setAppliedPromoCode: (state, action) => {
            state.appliedPromoCode = action.payload;
        },
        clearAppliedPromoCode: (state) => {
            state.appliedPromoCode = null;
            state.promoCodeData = null;
            state.promoCodeSuccess = null;
        },
        setSelectedZellePaymentId: (state, action) => {
            state.selectedZellePaymentId = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Apply promo code
            .addCase(applyPromoCode.pending, (state) => {
                state.promoCodeLoading = true;
                state.promoCodeError = null;
                state.promoCodeSuccess = null;
            })
            .addCase(applyPromoCode.fulfilled, (state, action) => {
                state.promoCodeLoading = false;
                state.promoCodeData = action.payload;
                state.promoCodeError = null;
                state.promoCodeSuccess =
                    action.payload.message || 'Promo code applied successfully!';
            })
            .addCase(applyPromoCode.rejected, (state, action) => {
                state.promoCodeLoading = false;
                state.promoCodeError = action.payload;
                state.promoCodeData = null;
                state.promoCodeSuccess = null;
            })
            // Remove promo code
            .addCase(removePromoCode.pending, (state) => {
                state.promoCodeLoading = true;
                state.promoCodeError = null;
                state.promoCodeSuccess = null;
            })
            .addCase(removePromoCode.fulfilled, (state, action) => {
                state.promoCodeLoading = false;
                state.promoCodeData = null;
                state.appliedPromoCode = null;
                state.promoCodeError = null;
                state.promoCodeSuccess =
                    action.payload.message || 'Promo code removed successfully!';
            })
            .addCase(removePromoCode.rejected, (state, action) => {
                state.promoCodeLoading = false;
                state.promoCodeError = action.payload;
                state.promoCodeSuccess = null;
            })
            // Fetch Zelle Payments
            .addCase(fetchZellePayments.pending, (state) => {
                state.zellePaymentsLoading = true;
                state.zellePaymentsError = null;
            })
            .addCase(fetchZellePayments.fulfilled, (state, action) => {
                state.zellePaymentsLoading = false;
                state.zellePayments = action.payload?.data || [];
            })
            .addCase(fetchZellePayments.rejected, (state, action) => {
                state.zellePaymentsLoading = false;
                state.zellePaymentsError = action.payload;
            });
    },
});

export const {
    setSelectedDeliveryDate,
    clearCheckoutData,
    clearError,
    clearPromoCodeError,
    clearPromoCodeSuccess,
    setAppliedPromoCode,
    clearAppliedPromoCode,
    setSelectedZellePaymentId,
} = checkoutSlice.actions;

// Selectors
export const selectDeliveryDates = (state) => state.checkout.deliveryDates;
export const selectSelectedDeliveryDate = (state) => state.checkout.selectedDeliveryDate;
export const selectCheckoutLoading = (state) => state.checkout.loading;
export const selectCheckoutError = (state) => state.checkout.error;
export const selectDeliveryCount = (state) => state.checkout.deliveryCount;

// Promo code selectors
export const selectPromoCodeData = (state) => state.checkout.promoCodeData;
export const selectPromoCodeLoading = (state) => state.checkout.promoCodeLoading;
export const selectPromoCodeError = (state) => state.checkout.promoCodeError;
export const selectPromoCodeSuccess = (state) => state.checkout.promoCodeSuccess;
export const selectAppliedPromoCode = (state) => state.checkout.appliedPromoCode;
export const selectZellePayments = (state) => state.checkout.zellePayments;
export const selectZellePaymentsLoading = (state) => state.checkout.zellePaymentsLoading;
export const selectSelectedZellePaymentId = (state) => state.checkout.selectedZellePaymentId;

export default checkoutSlice.reducer;
